import {
  codeSlashOutline,
  gitPullRequestOutline,
  logoBitbucket,
  person
} from 'ionicons/icons';

import { BB_CLIENT_ID } from '../constants/secrets';
import { ServiceTypes } from '../constants/service';
import {
  BitbucketCommits,
  BitbucketOrganisation,
  BitbucketRepo,
  BitbucketRepos,
  BitbucketUser,
  BitbucketUserEmails
} from '../types/bitbucket.types';
import { Commit, Organization, User } from '../types/services.types';
import { determineDefaultAvatarForUser } from '../util/defaultAvatarUrl';
import { ServiceError } from './errors';
import { get } from './requests';
import { Service } from './service';

export class Bitbucket implements Service {
  readonly id = ServiceTypes.BitBucket;
  readonly host: string;
  readonly name = 'Bitbucket';
  readonly isCloud: boolean;

  readonly #apiUrl: string;

  readonly emailsToIgnore = [];
  readonly tokenScopes = [
    'Account: Email',
    'Account: Read',
    'Repositories: Read',
    'Issues: Read',
    'Pull Requests: Read'
  ];
  readonly oauthScopes = [];
  readonly patName = 'App Password';
  readonly placeholderPat = 'App Password';
  readonly patRegex = `^.{1,${this.placeholderPat.length}}$`;
  readonly patUsername = true;
  readonly patUsernameLink = 'https://bitbucket.org/account/settings/';

  readonly orgText = 'workspace';
  readonly repoText = 'repository';

  readonly defaultAvatar = `/img/${this.id}.svg`;

  #username: string | null;
  #usingPat: boolean;

  constructor(host: string, usingPat: boolean, username: string | null) {
    this.host = host;
    this.isCloud = host === 'https://bitbucket.org';
    this.#usingPat = usingPat;
    this.#username = username;

    if (this.isCloud) this.#apiUrl = 'https://api.bitbucket.org/2.0';
    else this.#apiUrl = `${this.host}/api/2.0`;

    if (!this.isCloud && !username) {
      throw new ServiceError('Username is required for Bitbucket Server');
    }
  }

  getOAuthLink(state: string, codeChallenge: string): string {
    return `https://bitbucket.org/site/oauth2/authorize?client_id=${BB_CLIENT_ID}&response_type=code&state=${state}`;
  }

  getTokenUrl(org: string, ide: string, machine?: string): string {
    return `${this.host}/account/settings/app-passwords/new`;
  }

  async hasAccessToRepoProvided(
    token: string,
    org: string,
    repo: string | null
  ): Promise<boolean> {
    if (!repo) return true;
    try {
      await this.#get<BitbucketRepo>(token, `/repositories/${org}/${repo}`);

      return true;
    } catch (error: any) {
      return false;
    }
  }

  async getUser(token: string, org: string): Promise<User> {
    const currentUserResult = await this.#get<BitbucketUser>(token, `/user`);
    const userEmailsResult = await this.#get<BitbucketUserEmails>(
      token,
      `/user/emails`
    );
    const primaryEmail = userEmailsResult.values.find(
      (it) => it.is_confirmed && it.is_primary
    )?.email;
    if (!primaryEmail)
      throw new ServiceError(
        'No confirmed email address found',
        'Please confirm your email address in Bitbucket'
      );

    return {
      id: currentUserResult.uuid,
      photoURL: currentUserResult.links.avatar.href,
      displayName: currentUserResult.display_name,
      login: currentUserResult.username,
      email: primaryEmail,
      service: this.id
    };
  }

  async getUserEmails(token: string, org: string): Promise<string[]> {
    const userEmailsResult = await this.#get<BitbucketUserEmails>(
      token,
      `/user/emails`
    );

    return userEmailsResult.values.map((it) => it.email);
  }

  async getOrganization(token: string, org: string): Promise<Organization> {
    try {
      const orgResult = await this.#get<BitbucketOrganisation>(
        token,
        `/workspaces/${org}`
      );

      return {
        id: orgResult.uuid,
        photoURL: orgResult.links.avatar.href || this.defaultAvatar,
        displayName: orgResult.name,
        type: 'workspace',
        createdAt: orgResult.created_on,
        enterprise: !this.isCloud
      };
    } catch (error: any) {
      if (error.message.includes('Unexpected end of JSON input')) {
        throw new ServiceError(
          `No access to ${org}`,
          'Unable to find an workspace with the provided name. Please ensure you have access to the workspace and try again.'
        );
      }

      throw error;
    }
  }

  async getMostRecentRepos(token: string, org: string): Promise<string[]> {
    let lastUpdatedRepos = await this.#get<BitbucketRepos>(
      token,
      `/repositories/${org}?sort=-updated_on&q=is_private=true&pagelen=5`
    );
    if (lastUpdatedRepos.values.length === 0)
      lastUpdatedRepos = await this.#get<BitbucketRepos>(
        token,
        `/repositories/${org}?sort=-updated_on&q=is_private=false&pagelen=5`
      );

    return lastUpdatedRepos.values.map((it) => it.slug);
  }

  async getLatestCommitsFromRepos(
    token: string,
    org: string,
    repos: string[]
  ): Promise<Commit[][]> {
    const emailRegex = /\b[\w\.-]+@[\w\.-]+\.\w{2,}\b/;
    const nameRegex = /^[^<]*/;

    return await Promise.all(
      repos.map(async (repo) => {
        try {
          const latestCommits = await this.#get<BitbucketCommits>(
            token,
            `/repositories/${org}/${repo}/commits?pagelen=100`
          );

          return latestCommits.values.map(({ author: { raw, user } }) => {
            const emailMatches = raw.match(emailRegex);
            const nameMatches = raw.match(nameRegex);
            const email =
              emailMatches && emailMatches.length > 0 ? emailMatches[0] : '';
            const name = user?.display_name
              ? user?.display_name
              : nameMatches
                ? nameMatches[0]
                : '';

            return {
              email,
              name,
              photoURL:
                user?.links.avatar.href ||
                determineDefaultAvatarForUser(name, email)
            };
          });
        } catch (error: any) {
          console.warn(`Failed to fetch commits for ${org}/${repo}:`, error);

          return [];
        }
      })
    );
  }

  async getAvatar(
    token: string,
    email: string,
    placeholder: string,
    commitUrl: string
  ): Promise<string> {
    return placeholder;
  }

  getNonCloudAuthenticatedUrl(url: string): { url: string; useAuth: boolean } {
    const imageUrl = new URL(url);
    const hostUrl = new URL(this.host);

    return {
      useAuth: imageUrl.hostname === hostUrl.hostname,
      url
    };
  }

  getAuthorizationHeader(token: string): string {
    return `Bearer ${token}`;
  }

  readonly oauthPermissions = [
    {
      id: 'bitbucket_requests',
      icon: logoBitbucket,
      title: 'Requested OAuth scopes',
      subtitle:
        'When you authorize GitLive on your Bitbucket account we request permission to the following resources'
    },
    {
      id: 'scopes',
      icon: '',
      scopes: [
        {
          id: 'user_data',
          icon: person,
          title: 'Account information',
          subtitle: 'Read your account information',
          descriptionHTML:
            'Your email addresses are used to link your account with commit data from your git repository, and your primary email address is used to identify and contact you.' +
            '<p style="padding-top: 4px">Additionally, your name and avatar is used to display to the other members of your organization.</p>'
        },
        {
          id: 'repo',
          icon: codeSlashOutline,
          title: 'Repositories',
          subtitle: 'Read your repositories',
          descriptionHTML:
            'Repository data is accessed via the API to show the latest changes from your remote repository without you having to perform git fetch locally.' +
            '<p style="padding-top: 4px">It is also used to display branches from private repositories in the organization view.</p>'
        },
        {
          id: 'pull_requests',
          icon: gitPullRequestOutline,
          title: 'Pull Requests',
          subtitle: 'Read your pull requests',
          descriptionHTML:
            'Pull requests data is accessed via the API to show open pull requests in the repository and organization views.' +
            '<p style="padding-top: 4px">The data is also used to filter out branches that have had their pull request closed.</p>'
        }
      ]
    }
  ];

  readonly tokenPermissions = [
    {
      id: 'bitbucket_requests',
      icon: logoBitbucket,
      title: 'Required scopes',
      subtitle:
        'The following scopes are required when creating the app password'
    },
    {
      id: 'scopes',
      icon: '',
      scopes: [
        {
          id: 'user_data',
          icon: person,
          title: 'Account',
          subtitle: 'Read',
          descriptionHTML:
            'Your email addresses are used to link your account with commit data from your git repository, and your primary email address is used to identify and contact you.' +
            '<p style="padding-top: 4px">Additionally, your name and avatar is used to display to the other members of your organization.</p>'
        },
        {
          id: 'repo',
          icon: codeSlashOutline,
          title: 'Repositories',
          subtitle: 'Read',
          descriptionHTML:
            'Repository data is accessed via the API to show the latest changes from your remote repository without you having to perform git fetch locally.' +
            '<p style="padding-top: 4px">It is also used to display branches from private repositories in the organization view.</p>'
        },
        {
          id: 'pull_requests',
          icon: gitPullRequestOutline,
          title: 'Pull Requests',
          subtitle: 'Read',
          descriptionHTML:
            'Pull requests data is accessed via the API to show open pull requests in the repository and organization views.' +
            '<p style="padding-top: 4px">The data is also used to filter out branches that have had their pull request closed.</p>'
        }
      ]
    }
  ];

  async #get<T>(oauthToken: string, uri: string): Promise<T> {
    const token = this.#usingPat
      ? btoa(`${this.#username}:${oauthToken}`)
      : oauthToken;

    return await get(this.#apiUrl, token, uri, this.#usingPat);
  }
}
